import type { PlayEventMatch, PlayMatchResult } from '../types/playEvent';
import { sortPlayEventMatchesForDisplay } from './playEventMatchDisplay';

const ADVANCEMENT_RESULTS = new Set<PlayMatchResult>(['winner', 'walkover', 'retired', 'bye']);

export interface SingleEliminationAdvancementTarget {
  matchId: string;
  side: 'a' | 'b';
}

export function getSingleEliminationAdvancementSlot(
  roundNumber: number,
  indexInRound: number,
  roundCount: number
): { nextRoundNumber: number; nextIndexInRound: number; side: 'a' | 'b' } | null {
  if (roundNumber >= roundCount) {
    return null;
  }

  return {
    nextRoundNumber: roundNumber + 1,
    nextIndexInRound: Math.floor(indexInRound / 2),
    side: indexInRound % 2 === 0 ? 'a' : 'b',
  };
}

export function resolvePlayMatchWinnerParticipantId(
  match: Pick<
    PlayEventMatch,
    'result' | 'winnerSide' | 'sideAParticipantId' | 'sideBParticipantId'
  >
): string | null {
  if (match.result === 'bye') {
    if (match.winnerSide === 'a') return match.sideAParticipantId ?? null;
    if (match.winnerSide === 'b') return match.sideBParticipantId ?? null;
    return match.sideAParticipantId ?? match.sideBParticipantId ?? null;
  }

  if (!ADVANCEMENT_RESULTS.has(match.result) || !match.winnerSide) {
    return null;
  }

  return match.winnerSide === 'a'
    ? match.sideAParticipantId ?? null
    : match.sideBParticipantId ?? null;
}

export function findIndexInEliminationRound(
  matches: PlayEventMatch[],
  matchId: string,
  roundNumber: number
): number {
  const roundMatches = sortPlayEventMatchesForDisplay(
    matches.filter((match) => match.roundNumber === roundNumber)
  );
  return roundMatches.findIndex((match) => match.id === matchId);
}

export function findSingleEliminationAdvancementTarget(
  matches: PlayEventMatch[],
  completedMatch: PlayEventMatch,
  roundCount: number
): SingleEliminationAdvancementTarget | null {
  const indexInRound = findIndexInEliminationRound(
    matches,
    completedMatch.id,
    completedMatch.roundNumber
  );
  if (indexInRound < 0) {
    return null;
  }

  const slot = getSingleEliminationAdvancementSlot(
    completedMatch.roundNumber,
    indexInRound,
    roundCount
  );
  if (!slot) {
    return null;
  }

  const nextRoundMatches = sortPlayEventMatchesForDisplay(
    matches.filter((match) => match.roundNumber === slot.nextRoundNumber)
  );
  const targetMatch = nextRoundMatches[slot.nextIndexInRound];
  if (!targetMatch) {
    return null;
  }

  return {
    matchId: targetMatch.id,
    side: slot.side,
  };
}

export function getParticipantSideOnMatch(
  match: Pick<PlayEventMatch, 'sideAParticipantId' | 'sideBParticipantId'>,
  participantId: string
): 'a' | 'b' | null {
  if (match.sideAParticipantId === participantId) return 'a';
  if (match.sideBParticipantId === participantId) return 'b';
  return null;
}

/** Match ids from the immediate feeder through the final, following advancement edges. */
export function collectDownstreamSingleEliminationMatchIds(
  matches: PlayEventMatch[],
  sourceMatch: PlayEventMatch,
  roundCount: number
): string[] {
  const downstreamIds: string[] = [];
  let current: PlayEventMatch | undefined = sourceMatch;

  while (current) {
    const target = findSingleEliminationAdvancementTarget(matches, current, roundCount);
    if (!target) break;

    downstreamIds.push(target.matchId);
    current = matches.find((match) => match.id === target.matchId);
  }

  return downstreamIds;
}

/** Participant ids to clear from unscored downstream matches after a winner change. */
export function collectUnscoredDownstreamParticipantClears(
  matches: PlayEventMatch[],
  sourceMatch: PlayEventMatch,
  participantId: string,
  roundCount: number
): Array<{ matchId: string; side: 'a' | 'b' }> {
  const clears: Array<{ matchId: string; side: 'a' | 'b' }> = [];

  for (const matchId of collectDownstreamSingleEliminationMatchIds(
    matches,
    sourceMatch,
    roundCount
  )) {
    const downstreamMatch = matches.find((match) => match.id === matchId);
    if (!downstreamMatch || downstreamMatch.result !== 'unplayed') {
      continue;
    }

    const side = getParticipantSideOnMatch(downstreamMatch, participantId);
    if (side) {
      clears.push({ matchId, side });
    }
  }

  return clears;
}

const UPDATABLE_MATCH_RESULTS = new Set<PlayMatchResult>([
  'winner',
  'walkover',
  'retired',
  'cancelled',
]);

export function matchHasSideA(
  match: Pick<PlayEventMatch, 'sideAParticipantId' | 'sideATeamId'>
): boolean {
  return Boolean(match.sideAParticipantId || match.sideATeamId);
}

export function matchHasSideB(
  match: Pick<PlayEventMatch, 'sideBParticipantId' | 'sideBTeamId'>
): boolean {
  return Boolean(match.sideBParticipantId || match.sideBTeamId);
}

function matchHasBothSides(
  match: Pick<
    PlayEventMatch,
    'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >
): boolean {
  return matchHasSideA(match) && matchHasSideB(match);
}

/** One player assigned; opponent slot still open or waiting on a feeder match. */
export function getPopulatedPlayEventMatchSide(
  match: Pick<
    PlayEventMatch,
    'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >
): 'a' | 'b' | null {
  const hasSideA = matchHasSideA(match);
  const hasSideB = matchHasSideB(match);
  if (hasSideA && !hasSideB) return 'a';
  if (hasSideB && !hasSideA) return 'b';
  return null;
}

function matchIsScoreable(
  match: Pick<
    PlayEventMatch,
    'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >
): boolean {
  return matchHasBothSides(match) || getPopulatedPlayEventMatchSide(match) != null;
}

export function canScorePlayEventMatch(
  match: Pick<
    PlayEventMatch,
    'result' | 'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >
): boolean {
  if (match.result !== 'unplayed') {
    return false;
  }

  return matchIsScoreable(match);
}

export function canUpdatePlayEventMatchScore(
  match: Pick<
    PlayEventMatch,
    'result' | 'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >
): boolean {
  if (!UPDATABLE_MATCH_RESULTS.has(match.result)) {
    return false;
  }

  return matchIsScoreable(match);
}

export function formatPlayEventMatchScoreLabel(
  score: PlayEventMatch['score'] | null | undefined
): string | null {
  if (!score?.sets?.length) {
    return null;
  }
  return score.sets.map((set) => `${set.a}-${set.b}`).join(', ');
}

export function resolveSingleEliminationPodium(
  matches: PlayEventMatch[],
  roundCount: number
): { winnerParticipantId: string | null; runnerUpParticipantId: string | null } {
  const finalRoundMatches = sortPlayEventMatchesForDisplay(
    matches.filter((match) => match.roundNumber === roundCount)
  );
  const finalMatch = finalRoundMatches[0];
  if (!finalMatch || finalMatch.result === 'unplayed') {
    return { winnerParticipantId: null, runnerUpParticipantId: null };
  }

  const winnerParticipantId = resolvePlayMatchWinnerParticipantId(finalMatch);
  if (!winnerParticipantId || !finalMatch.winnerSide) {
    return { winnerParticipantId: null, runnerUpParticipantId: null };
  }

  const runnerUpParticipantId =
    finalMatch.winnerSide === 'a'
      ? finalMatch.sideBParticipantId ?? null
      : finalMatch.sideAParticipantId ?? null;

  return { winnerParticipantId, runnerUpParticipantId };
}
